import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useDeliveryTypes, useSubmitOrder, useVerifyPayment, useMarkPaymentFailed } from '../hooks/useQueries';
import { getSessionId, trackEvent } from '../utils/analytics';
import './Checkout.css';

const STEPS = ['Address & Details', 'Review & Pay'];

const initialForm = {
  firstName: '', lastName: '', email: '', phone: '',
  address: '', city: '', state: '', pincode: '', country: 'India',
};

// ─────────────────────────────────────────────────────────────────────────────
// Payload builder
// ─────────────────────────────────────────────────────────────────────────────
const buildCheckoutPayload = ({ form, selectedDeliveryId, items }) => {
  const customer = {
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
  };

  const address = {
    address_line: form.address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    pincode: form.pincode.trim(),
    country: form.country,
  };

  const mappedItems = items.map((item) => {
    const norm = (s) => (s ?? '').toString().trim().toLowerCase();

    // Use pre-resolved records from CartContext (supports multiple: color + size)
    // Fall back to single-record lookup for backward compat
    const variantRecords =
      item.selected_variant_records?.length > 0
        ? item.selected_variant_records
        : item.selected_variant_record
          ? [item.selected_variant_record]
          : item.variant
            ? item.variant.split(' / ').map(part =>
              (item.product_variants || []).find(
                v => norm(v.variant_value) === norm(part.trim()) || norm(v.variant_name) === norm(part.trim())
              ) ?? null
            ).filter(Boolean)
            : [];

    const variants = variantRecords.map(r => ({
      variant_type_id: r.variant_type_id,
      variant_type_name: r.product_variant_type?.name || r.variant_type?.name || r.variant_type_name || r.variant_name || '',
      variant_value: r.variant_value,
      price_adjustment: Number(r.price_adjustment || 0),
    }));

    const addons = [];
    if (item.giftWrap) {
      const giftAddon = (item.addons || []).find(
        (a) => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
      );
      if (giftAddon) {
        addons.push({
          addon_id: giftAddon.product_addons?.addon_id ?? giftAddon.id,
        });
      }
    }

    const customizations = (item.customizations_payload || []).map((cf) => ({
      field_id: cf.field_id,
      value: Array.isArray(cf.value) ? cf.value.join(', ') : String(cf.value ?? ''),
      price: Number(cf.price_adjustment || 0),
    }));

    return {
      product_id: item.id,
      quantity: item.qty,
      variants,
      addons,
      customizations,
    };
  });

  return {
    customer,
    address,
    delivery_type_id: selectedDeliveryId,
    analytics_session_id: getSessionId(),
    items: mappedItems,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const Checkout = () => {
  const { items, subtotal, giftWrapTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [placedOrder, setPlacedOrder] = useState(null);

  // ── Read logged-in customer from localStorage ──────────────────────────────
  const [loggedInCustomer, setLoggedInCustomer] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('customer'));
      if (stored) {
        setLoggedInCustomer(stored);
        // Pre-fill form fields silently from localStorage
        setForm((f) => ({
          ...f,
          firstName: stored.firstName || '',
          lastName: stored.lastName || '',
          email: stored.email || '',
          phone: stored.phone || '',
        }));
      }
    } catch {
      // not logged in — form stays empty, user fills manually
    }
  }, []);

  const isLoggedIn = Boolean(loggedInCustomer);

  const {
    mutateAsync: placeOrder,
    isPending: isSubmitting,
    reset: resetMutation,
  } = useSubmitOrder();

  const { mutateAsync: verifyPayment } = useVerifyPayment();
  const { mutateAsync: failPayment } = useMarkPaymentFailed();

  // Load Razorpay script once
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const {
    data: deliveryTypes = [],
    isLoading: deliveryLoading,
    isError: deliveryIsError,
  } = useDeliveryTypes();

  const deliveryError = deliveryIsError
    ? 'Could not load delivery options. Please refresh.'
    : null;

  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  useEffect(() => {
    if (deliveryTypes.length > 0 && selectedDeliveryId === null) {
      setSelectedDeliveryId(deliveryTypes[0].id);
    }
  }, [deliveryTypes, selectedDeliveryId]);

  const selectedDelivery = deliveryTypes.find((d) => d.id === selectedDeliveryId) ?? null;
  const deliveryCharge = selectedDelivery?.charge ?? 0;
  const grandTotal = subtotal + giftWrapTotal + deliveryCharge;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // ── Validation: skip customer fields if logged in ──────────────────────────
  const validateStep1 = () => {
    const e = {};
    // Only validate customer fields if NOT logged in
    if (!isLoggedIn) {
      if (!form.firstName.trim()) e.firstName = 'Required';
      if (!form.lastName.trim()) e.lastName = 'Required';
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
      if (!form.phone.trim() || form.phone.length < 10) e.phone = 'Valid phone required';
    }
    if (!form.address.trim()) e.address = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.state.trim()) e.state = 'Required';
    if (!form.pincode.trim() || form.pincode.length < 6) e.pincode = 'Valid pincode required';
    if (!selectedDeliveryId) e.delivery = 'Please select a delivery option';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => true; // Razorpay popup handles payment validation

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  };

  const handlePlaceOrder = async () => {
    setApiError('');
    resetMutation();
    try {
      // Step 1: Create order on backend → get Razorpay order id
      const payload = buildCheckoutPayload({ form, selectedDeliveryId, items });
      const orderData = await placeOrder(payload);
      // orderData contains: { order_id, razorpay_order_id, amount, currency, customer }

      // Step 2: Open Razorpay checkout popup
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'F Creation',
        description: 'Order Payment',
        order_id: orderData.razorpay_order_id,
        prefill: {
          name: orderData.customer?.name || `${form.firstName} ${form.lastName}`.trim(),
          email: orderData.customer?.email || form.email,
          contact: orderData.customer?.phone || form.phone,
        },
        theme: { color: '#c8956c' },
        modal: {
          ondismiss: async () => {
            if (paymentAttemptFailed) return; // already handled by payment.failed
            try {
              await failPayment({
                razorpay_order_id: orderData.razorpay_order_id,
                reason: 'Payment cancelled by user',
              });
            } catch { /* silent */ }
            setApiError('Payment was cancelled. You can try again.');
            trackEvent('PAYMENT_FAILED', { metadata: { reason: 'Payment cancelled by user' } });
          },
        },
        handler: async (response) => {
          try {
            const paymentResult = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPlacedOrder(paymentResult);
            trackEvent('PAYMENT_SUCCESS', { order_id: paymentResult.order_id ?? paymentResult.id });
            trackEvent('ORDER_PLACED', { order_id: paymentResult.order_id ?? paymentResult.id, metadata: { total: grandTotal } });
            clearCart();
            window.scrollTo(0, 0);
          } catch {
            setApiError('Payment verification failed. Please contact support with your payment ID: ' + response.razorpay_payment_id);
            window.scrollTo(0, 0);
          }
        },
      };

      const rzp = new window.Razorpay(options);

      let paymentAttemptFailed = false;

      rzp.on('payment.failed', async (response) => {
        paymentAttemptFailed = true;
        try {
          await failPayment({
            razorpay_order_id: orderData.razorpay_order_id,
            reason: response.error?.description || 'Payment failed',
          });
        } catch { /* silent */ }
        setApiError(`Payment failed: ${response.error?.description || 'Unknown error'}. Please try again.`);
        trackEvent('PAYMENT_FAILED', { metadata: { reason: response.error?.description || 'Unknown error' } });
        window.scrollTo(0, 0);
      });

      rzp.open();

    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Something went wrong. Please try again.';
      setApiError(msg);
      window.scrollTo(0, 0);
    }
  };



  // ── Empty cart ─────────────────────────────────────────────────────────────
  if (items.length === 0 && !placedOrder) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h3>Your cart is empty</h3>
            <p>Add some gifts before checking out!</p>
            <Link to="/products" className="btn btn-primary-warm btn-lg">Browse Gifts</Link>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (items.length > 0) trackEvent('CHECKOUT_START', { metadata: { item_count: items.length, subtotal } });
  }, []);

  // ── Order success ───────────────────────────────────────────────────────────
  if (placedOrder) {
    const statusColors = {
      confirmed: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
      pending: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
      paid: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
      unpaid: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    };

    const orderStatus = placedOrder.status ?? 'confirmed';
    const paymentStatus = placedOrder.payment_status ?? 'paid';
    const paymentMethod = placedOrder.payment_method ?? form.paymentMethod;
    const orderId = placedOrder.order_id ?? placedOrder.id;
    const totalAmount = placedOrder.total_amount ?? grandTotal;

    const orderStyle = statusColors[orderStatus] ?? statusColors.confirmed;
    const paymentStyle = statusColors[paymentStatus] ?? statusColors.paid;

    return (
      <div className="checkout-page">
        <div className="container">
          <div className="order-success">
            <div className="order-success__icon">🎉</div>
            <h1 className="order-success__title">Order Placed Successfully!</h1>
            <p className="order-success__sub">
              Thank you for your order. We're preparing your gifts with love and care!
            </p>
            <div className="order-success__card">
              <div className="order-detail-row">
                <span>Order Number</span>
                <strong>#{orderId ?? '—'}</strong>
              </div>
              <div className="order-detail-row">
                <span>Order Status</span>
                <span style={{
                  display: 'inline-block',
                  padding: '3px 12px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  background: orderStyle.bg,
                  color: orderStyle.color,
                  border: `1px solid ${orderStyle.border}`,
                }}>
                  {orderStatus}
                </span>
              </div>
              <div className="order-detail-row">
                <span>Payment Status</span>
                <span style={{
                  display: 'inline-block',
                  padding: '3px 12px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  background: paymentStyle.bg,
                  color: paymentStyle.color,
                  border: `1px solid ${paymentStyle.border}`,
                }}>
                  {paymentStatus}
                </span>
              </div>
              <div className="order-detail-row">
                <span>Payment Method</span>
                <strong style={{ textTransform: 'capitalize' }}>{paymentMethod}</strong>
              </div>
              <div className="order-detail-row">
                <span>Delivery via</span>
                <strong>{selectedDelivery?.name ?? 'Standard'}</strong>
              </div>
              <div className="order-detail-row">
                <span>Total Paid</span>
                <strong>₹{Number(totalAmount).toLocaleString('en-IN')}</strong>
              </div>
              <div className="order-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span>Track your order</span>
                <strong style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  You can view your order details inside the{' '}
                  <span style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}>
                    My Orders
                  </span>{' '}
                  section after logging in with your email.
                </strong>
              </div>
            </div>
            <div className="order-success__actions">
              <Link to="/account" state={{ tab: 'Orders history' }} className="btn btn-primary-warm btn-lg">View My Orders</Link>
              <Link to="/products" className="btn btn-outline btn-lg">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main checkout ───────────────────────────────────────────────────────────
  return (
    <div className="checkout-page">
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link><span>›</span>
            <Link to="/cart">Cart</Link><span>›</span>
            <span>Checkout</span>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Step progress */}
        <div className="checkout-progress">
          {STEPS.map((label, i) => {
            const s = i + 1;
            return (
              <React.Fragment key={s}>
                <div className={`progress-step ${step > s ? 'done' : step === s ? 'active' : ''}`}>
                  <div className="progress-step__circle">{step > s ? '✓' : s}</div>
                  <span className="progress-step__label">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`progress-line ${step > s ? 'done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* API error banner */}
        {apiError && (
          <div className="api-error-banner" style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
            padding: '12px 16px', marginBottom: 20, color: '#dc2626', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            ⚠️ {apiError}
          </div>
        )}

        <div className="checkout-layout">
          {/* ── Form Area ── */}
          <div className="checkout-form-wrap">

            {/* Step 1: Address + Delivery */}
            {step === 1 && (
              <div className="checkout-section">
                <h2 className="checkout-section__title">Delivery Details</h2>

                {/* ── Logged-in customer info banner ── */}
                {isLoggedIn && (
                  <div className="checkout-customer-banner">
                    <div className="checkout-customer-banner__avatar">
                      {`${loggedInCustomer.firstName?.[0] ?? ''}${loggedInCustomer.lastName?.[0] ?? ''}`.toUpperCase()}
                    </div>
                    <div className="checkout-customer-banner__info">
                      <p className="checkout-customer-banner__name">
                        {loggedInCustomer.firstName} {loggedInCustomer.lastName}
                      </p>
                      <p className="checkout-customer-banner__meta">
                        {loggedInCustomer.email}
                        {loggedInCustomer.phone && <> · {loggedInCustomer.phone}</>}
                      </p>
                    </div>
                    <Link to="/account" className="checkout-customer-banner__edit">
                      Edit profile →
                    </Link>
                  </div>
                )}

                {/* ── Customer fields — only shown when NOT logged in ── */}
                {!isLoggedIn && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>First Name *</label>
                        <input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="Rahul" className={errors.firstName ? 'error' : ''} />
                        {errors.firstName && <span className="form-error">{errors.firstName}</span>}
                      </div>
                      <div className="form-group">
                        <label>Last Name *</label>
                        <input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Sharma" className={errors.lastName ? 'error' : ''} />
                        {errors.lastName && <span className="form-error">{errors.lastName}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="rahul@example.com" className={errors.email ? 'error' : ''} />
                        {errors.email && <span className="form-error">{errors.email}</span>}
                      </div>
                      <div className="form-group">
                        <label>Phone Number *</label>
                        <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 98765 43210" className={errors.phone ? 'error' : ''} />
                        {errors.phone && <span className="form-error">{errors.phone}</span>}
                      </div>
                    </div>
                  </>
                )}

                {/* ── Address fields — always shown ── */}
                <div className="form-group">
                  <label>Delivery Address *</label>
                  <textarea value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Flat/House No., Street, Area, Landmark" rows={3} className={errors.address ? 'error' : ''} />
                  {errors.address && <span className="form-error">{errors.address}</span>}
                </div>

                <div className="form-row form-row--3">
                  <div className="form-group">
                    <label>City *</label>
                    <input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Mumbai" className={errors.city ? 'error' : ''} />
                    {errors.city && <span className="form-error">{errors.city}</span>}
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <select value={form.state} onChange={(e) => set('state', e.target.value)} className={errors.state ? 'error' : ''}>
                      <option value="">Select State</option>
                      {[
                        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
                        'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
                        'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
                        'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
                        'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
                        'Uttarakhand', 'West Bengal',
                      ].map((s) => <option key={s}>{s}</option>)}
                    </select>
                    {errors.state && <span className="form-error">{errors.state}</span>}
                  </div>
                  <div className="form-group">
                    <label>Pincode *</label>
                    <input value={form.pincode} onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="400001" className={errors.pincode ? 'error' : ''} />
                    {errors.pincode && <span className="form-error">{errors.pincode}</span>}
                  </div>
                </div>

                {/* Delivery Type Selection */}
                <div className="checkout-section__subtitle" style={{ marginTop: 24 }}>Delivery Options</div>
                {deliveryLoading && (
                  <div className="delivery-loading">
                    <span className="delivery-spinner" /> Loading delivery options…
                  </div>
                )}
                {deliveryError && <div className="delivery-error">{deliveryError}</div>}
                {!deliveryLoading && !deliveryError && deliveryTypes.length === 0 && (
                  <div className="delivery-error">No delivery options available.</div>
                )}
                {!deliveryLoading && !deliveryError && deliveryTypes.length > 0 && (
                  <div className="delivery-options">
                    {deliveryTypes.map((d) => {
                      const isFree = d.charge === 0;
                      const isSelected = selectedDeliveryId === d.id;
                      return (
                        <label key={d.id} className={`delivery-option ${isSelected ? 'active' : ''}`} onClick={() => setSelectedDeliveryId(d.id)}>
                          <input type="radio" name="deliveryType" value={d.id} checked={isSelected} onChange={() => setSelectedDeliveryId(d.id)} />
                          <div className="delivery-option__content">
                            <div className="delivery-option__left">
                              <span className="delivery-option__name">{d.name}</span>
                              {d.description && <span className="delivery-option__desc">{d.description}</span>}
                              {d.estimatedDays && <span className="delivery-option__eta">🕐 {d.estimatedDays}</span>}
                            </div>
                            <span className={`delivery-option__price ${isFree ? 'free-tag' : ''}`}>
                              {isFree ? 'FREE' : `₹${d.charge.toLocaleString('en-IN')}`}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {errors.delivery && (
                  <span className="form-error" style={{ marginTop: 4, display: 'block' }}>{errors.delivery}</span>
                )}

                <button className="btn btn-primary-warm btn-lg" style={{ marginTop: 24, width: '100%' }} onClick={handleNext}>
                  Continue to Payment →
                </button>
              </div>
            )}

            {/* Step 2: Review & Pay */}
            {step === 2 && (
              <div className="checkout-section">
                <h2 className="checkout-section__title">Review Your Order</h2>

                <div className="review-block">
                  <div className="review-block__header">
                    <span>Delivery to</span>
                    <button onClick={() => setStep(1)} className="review-edit">Edit</button>
                  </div>
                  <p className="review-block__value">{form.firstName} {form.lastName}</p>
                  <p className="review-block__sub">{form.address}, {form.city}, {form.state} – {form.pincode}</p>
                  <p className="review-block__sub">{form.phone} · {form.email}</p>
                </div>

                <div className="review-block">
                  <div className="review-block__header">
                    <span>Delivery Method</span>
                    <button onClick={() => setStep(1)} className="review-edit">Edit</button>
                  </div>
                  <p className="review-block__value">{selectedDelivery?.name ?? '—'}</p>
                  {selectedDelivery?.estimatedDays && (
                    <p className="review-block__sub">🕐 {selectedDelivery.estimatedDays}</p>
                  )}
                </div>

                <div className="review-items">
                  <h3>Order Items ({items.length})</h3>
                  {items.map((item) => (
                    <div key={item.cartItemId} className="review-item">
                      <img src={item.image || item.images[0]} alt={item.name} className="review-item__img" />
                      <div className="review-item__info">
                        <p className="review-item__name">{item.name}</p>
                        <p className="review-item__meta">
                          {item.selected_variant_records?.length > 1
                            ? item.selected_variant_records.map(r =>
                              `${r.product_variant_type?.name || r.variant_type?.name || 'Option'}: ${r.variant_name || r.variant_value}`
                            ).join(' · ')
                            : item.selected_variant_record
                              ? `${item.selected_variant_record.product_variant_type?.name || item.selected_variant_record.variant_type?.name || 'Option'}: ${item.selected_variant_record.variant_name || item.selected_variant_record.variant_value}`
                              : item.variant && item.variant !== 'Default'
                                ? item.variant
                                : null
                          }
                          {' '}· Qty: {item.qty}
                        </p>
                        {item.variantPriceAdjustment > 0 && (
                          <p className="review-item__meta" style={{ color: 'var(--primary)' }}>
                            +₹{item.variantPriceAdjustment.toLocaleString('en-IN')} for selected option
                          </p>
                        )}
                        {item.giftWrap && (
                          <p className="review-item__meta" style={{ color: 'var(--primary)' }}>
                            🎀 Gift Wrapping included
                          </p>
                        )}
                        {item.customizations_payload?.map((cf) => {
                          const displayVal = Array.isArray(cf.value) ? cf.value.join(', ') : cf.value;
                          return (
                            <p key={cf.field_id} className="review-item__meta" style={{ fontSize: 12 }}>
                              {cf.field_label}: <strong>{displayVal}</strong>
                              {Number(cf.price_adjustment) > 0 && (
                                <span style={{ color: 'var(--primary)' }}>
                                  {' '}(+₹{Number(cf.price_adjustment).toLocaleString('en-IN')})
                                </span>
                              )}
                            </p>
                          );
                        })}
                      </div>
                      <p className="review-item__price">₹{(item.price * item.qty).toLocaleString()}</p>
                    </div>
                  ))}
                </div>

                {/* Razorpay secure payment note */}
                <div style={{
                  background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
                  padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#166534',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  🔒 Payment is processed securely via Razorpay. Supports Card, UPI, Net Banking & Wallets.
                </div>

                <div className="checkout-nav">
                  <button className="btn btn-outline" onClick={() => setStep(1)} disabled={isSubmitting}>
                    ← Back
                  </button>
                  <button
                    className="btn btn-primary-warm btn-lg"
                    onClick={handlePlaceOrder}
                    disabled={isSubmitting}
                    style={{ minWidth: 240, position: 'relative' }}
                  >
                    {isSubmitting ? (
                      <><span className="delivery-spinner" style={{ marginRight: 8 }} />Creating Order…</>
                    ) : (
                      `🔒 Pay ₹${grandTotal.toLocaleString()} via Razorpay`
                    )}
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* ── Order Summary Sidebar ── */}
          <div className="checkout-summary">
            <h3 className="checkout-summary__title">Order Summary</h3>
            <div className="checkout-summary__items">
              {items.map((item) => (
                <div key={item.cartItemId} className="checkout-summary__item">
                  <div className="checkout-summary__item-img">
                    <img src={item.image || item.images[0]} alt={item.name} />
                    <span className="checkout-summary__qty">{item.qty}</span>
                  </div>
                  <div className="checkout-summary__item-info">
                    <p className="checkout-summary__item-name">{item.name}</p>
                    <p className="checkout-summary__item-meta">
                      {item.selected_variant_records?.length > 1
                        ? item.selected_variant_records.map(r =>
                          `${r.product_variant_type?.name || r.variant_type?.name || 'Option'}: ${r.variant_name || r.variant_value}`
                        ).join(' · ')
                        : item.selected_variant_record
                          ? `${item.selected_variant_record.product_variant_type?.name || item.selected_variant_record.variant_type?.name || 'Option'}: ${item.selected_variant_record.variant_name || item.selected_variant_record.variant_value}`
                          : item.variant && item.variant !== 'Default'
                            ? item.variant
                            : null
                      }
                    </p>
                    {item.variantPriceAdjustment > 0 && (
                      <p className="checkout-summary__item-meta" style={{ color: 'var(--primary)', fontSize: 11 }}>
                        +₹{item.variantPriceAdjustment.toLocaleString('en-IN')} for selected option
                      </p>
                    )}
                    {item.giftWrap && (
                      <p className="checkout-summary__item-meta" style={{ color: 'var(--primary)', fontSize: 11 }}>
                        🎀 Gift Wrapping
                      </p>
                    )}
                    {item.customizations_payload?.map((cf) => {
                      const displayVal = Array.isArray(cf.value) ? cf.value.join(', ') : cf.value;
                      return (
                        <p key={cf.field_id} className="checkout-summary__item-meta" style={{ fontSize: 11 }}>
                          {cf.field_label}: <strong>{displayVal}</strong>
                          {Number(cf.price_adjustment) > 0 && (
                            <span style={{ color: 'var(--primary)' }}>
                              {' '}(+₹{Number(cf.price_adjustment).toLocaleString('en-IN')})
                            </span>
                          )}
                        </p>
                      );
                    })}
                  </div>
                  <p className="checkout-summary__item-price">₹{(item.price * item.qty).toLocaleString()}</p>
                </div>
              ))}
            </div>

            <div className="checkout-summary__totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>
              {items.filter(i => i.giftWrap).map(i => {
                const addon = (i.addons || []).find(
                  a => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
                );
                const unitPrice = addon
                  ? Number(addon.product_addons?.custom_price ?? addon.price ?? 0)
                  : 0;
                const lineTotal = unitPrice * i.qty;
                return (
                  <div key={`giftwrap-${i.cartItemId}`} className="summary-row">
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span>🎀 Gift Wrapping</span>
                      <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                        ₹{unitPrice.toLocaleString('en-IN')} × {i.qty} {i.qty > 1 ? 'pcs' : 'pc'}
                      </span>
                    </span>
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                      ₹{lineTotal.toLocaleString('en-IN')}
                    </span>
                  </div>
                );
              })}
              <div className="summary-row">
                <span>Delivery</span>
                {deliveryLoading ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</span>
                ) : selectedDelivery ? (
                  <span className={deliveryCharge === 0 ? 'free-tag' : ''}>
                    {deliveryCharge === 0 ? 'FREE' : `₹${deliveryCharge.toLocaleString('en-IN')}`}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Select option</span>
                )}
              </div>
              <div className="summary-divider" />
              <div className="summary-row summary-row--total">
                <span>Total</span>
                <span>₹{grandTotal.toLocaleString()}</span>
              </div>
            </div>

            <div className="checkout-summary__secure">
              🔒 <span>All transactions are secure and encrypted</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;